import lodash from 'lodash';
import { FiInfo } from 'react-icons/fi';
import React, { useEffect, useState } from 'react';

import MyTeamContent from './components/MyTeamContent';
import PopOver from 'components/common/popover';

import { MY_TEAM_FILTERS } from 'constants/myTeam';
import { DEFAULT_PAGE, MAX_PAGE_SIZE } from 'constants/page';

import { handleError } from 'utils/errorHandler';

import * as userService from 'services/user';
import { getWithPossessionSuffix } from 'utils/string';
import { EmployeeLike } from 'interface/userUtils';

interface TeamMember {
  user: {
    id: number;
    fullname: string;
  };
  currentAllocations?: Array<{ appraiserId?: number; coAppraiserId?: number; name: string }> | null;
}

interface MyTeamProps {
  employee: EmployeeLike & { id: number; firstName: string };
  isSelf: boolean;
}

interface TeamBuckets {
  issuerTeam: TeamMember[];
  managerTeam: TeamMember[];
  appraiserTeam: TeamMember[];
  coAppraiserTeam: TeamMember[];
}

const MyTeam: React.FC<MyTeamProps> = (props) => {
  const { employee, isSelf } = props;

  const { id: employeeId } = employee;
  const [teamBuckets, setTeamBuckets] = useState<TeamBuckets>({
    issuerTeam: [],
    managerTeam: [],
    appraiserTeam: [],
    coAppraiserTeam: [],
  });
  const [loadedEmployeeId, setLoadedEmployeeId] = useState<number | null>(null);

  const [activeFilter, setActiveFilter] = useState(MY_TEAM_FILTERS.ISSUER);

  const HeaderContent = isSelf ? 'Viewing my team as' : `Viewing ${getWithPossessionSuffix(employee.firstName)} team`;
  const HeaderInfo = isSelf
    ? 'The list of Leapfroggers for whom I am responsible for.'
    : `The list of Leapfroggers for whom ${employee.firstName} is responsible for.`;

  const isLoading = loadedEmployeeId !== employeeId;
  const visibleTeams =
    loadedEmployeeId === employeeId
      ? teamBuckets
      : {
          issuerTeam: [],
          managerTeam: [],
          appraiserTeam: [],
          coAppraiserTeam: [],
        };

  useEffect(() => {
    let isActive = true;

    const fetchTeamMembers = async (relations: string) => {
      const params = { page: DEFAULT_PAGE, size: MAX_PAGE_SIZE, relations };
      const response = (await userService.fetchTeamMembers(employeeId, params)) as {
        data?: TeamMember[];
        meta?: { page: number; size: number; total: number };
      };
      const meta = response.meta || { page: DEFAULT_PAGE, size: MAX_PAGE_SIZE, total: 0 };

      const { page, size: pageSize, total } = meta;

      const totalPages = Math.ceil(total / pageSize);

      const fetchRemainingPromise = [];

      for (let i = page + 1; i <= totalPages; i++) {
        fetchRemainingPromise.push(
          userService.fetchTeamMembers(employeeId, { page: i, size: MAX_PAGE_SIZE, relations })
        );
      }

      const remainingTeam = (await Promise.all(fetchRemainingPromise)) as Array<{ data?: TeamMember[] }>;

      return [
        ...(response.data || []),
        ...remainingTeam.reduce<TeamMember[]>((acc, { data }) => [...acc, ...(data || [])], []),
      ];
    };

    const fetchAllTeamMembers = async () => {
      const [issuerResult, managerResult, appraiserResult, coAppraiserResult] = await Promise.allSettled([
        fetchTeamMembers('leaveIssuer'),
        fetchTeamMembers('teamManager'),
        fetchTeamMembers('appraiser'),
        fetchTeamMembers('coAppraiser'),
      ]);

      if (!isActive) {
        return;
      }

      const nextTeamBuckets: TeamBuckets = {
        issuerTeam: [],
        managerTeam: [],
        appraiserTeam: [],
        coAppraiserTeam: [],
      };

      if (issuerResult.status === 'fulfilled') {
        nextTeamBuckets.issuerTeam = lodash.orderBy(issuerResult.value, [(item) => item.user.fullname.toLowerCase()]);
      } else {
        handleError(issuerResult.reason);
      }

      if (managerResult.status === 'fulfilled') {
        nextTeamBuckets.managerTeam = lodash.sortBy(managerResult.value, [(item) => item.user.fullname.toLowerCase()]);
      } else {
        handleError(managerResult.reason);
      }

      if (appraiserResult.status === 'fulfilled') {
        const uniqueTeam = appraiserResult.value.reduce<TeamMember[]>((acc, curr) => {
          if (acc.findIndex((item) => item.user.id === curr.user.id) >= 0) {
            return acc;
          }

          const allocations = (curr.currentAllocations || []).filter(
            (allocation) => allocation.appraiserId === employeeId
          );

          return [...acc, { ...curr, currentAllocations: allocations }];
        }, []);

        nextTeamBuckets.appraiserTeam = lodash.sortBy(uniqueTeam, [(item) => item.user.fullname.toLowerCase()]);
      } else {
        handleError(appraiserResult.reason);
      }

      if (coAppraiserResult.status === 'fulfilled') {
        const uniqueTeam = coAppraiserResult.value.reduce<TeamMember[]>((acc, curr) => {
          if (acc.findIndex((item) => item.user.id === curr.user.id) >= 0) {
            return acc;
          }

          const allocations = (curr.currentAllocations || []).filter(
            (allocation) => allocation.coAppraiserId === employeeId
          );

          return [...acc, { ...curr, currentAllocations: allocations }];
        }, []);

        nextTeamBuckets.coAppraiserTeam = lodash.sortBy(uniqueTeam, [(item) => item.user.fullname.toLowerCase()]);
      } else {
        handleError(coAppraiserResult.reason);
      }

      setTeamBuckets(nextTeamBuckets);
      setLoadedEmployeeId(employeeId);
    };

    void fetchAllTeamMembers();

    return () => {
      isActive = false;
    };
  }, [employeeId]);

  if (isLoading) {
    return null;
  }

  if (
    !visibleTeams.issuerTeam.length &&
    !visibleTeams.managerTeam.length &&
    !visibleTeams.appraiserTeam.length &&
    !visibleTeams.coAppraiserTeam.length
  ) {
    return null;
  }

  return (
    <div className="full-scope-card my-team-list mt-0 moml-30">
      <div className="my-team-list__label d-flex">
        <div className="pr-5">{HeaderContent}</div>
        <div className="my-team-list__icon">
          <PopOver
            interactive
            html={
              <div className="tooltip-info">
                <div className="tooltip-info__my-team-help d-flex">
                  <p>{HeaderInfo}</p>
                </div>
              </div>
            }
          >
            <FiInfo size={14} />
          </PopOver>
        </div>
      </div>
      <MyTeamContent
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        issuerTeam={visibleTeams.issuerTeam as never}
        managerTeam={visibleTeams.managerTeam as never}
        appraiserTeam={visibleTeams.appraiserTeam as never}
        coAppraiserTeam={visibleTeams.coAppraiserTeam as never}
        isLoading={isLoading}
      />
    </div>
  );
};

export default MyTeam;
